import React, { useEffect, useState } from "react";
import { Link, Redirect, useHistory } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowBack, Save } from "@material-ui/icons";
import firebase from "../firebase";
import { palette } from "../theme";
import PageContainer from "./UI/PageContainer";
import PageHeader from "./UI/PageHeader";

// Kids are encoded as "kidId_Full Name" in state and in Firestore (Groups.Kids),
// matching the original data shape so existing records keep working.
const kidId = (encoded) => encoded.split("_")[0];
const kidName = (encoded) => encoded.split("_")[1];

const toInputDate = (d) => {
  if (!(d instanceof Date) || isNaN(d)) return "";
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

const fromInputDate = (value) => {
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y, m - 1, d);
};

export default function GroupSessionsForm() {
  const history = useHistory();
  const db = firebase.firestore();
  const signedIn = global.config.uid !== "";

  const params = new URLSearchParams(window.location.search);
  const initialGroupID = params.get("groupID") || "";

  const [groupID, setGroupID] = useState(initialGroupID);
  const [kids, setKids] = useState([]); // ["kidId_Full Name", ...]
  const [notesByKid, setNotesByKid] = useState({}); // { kidId: notes }
  const [date, setDate] = useState(new Date());
  const [location, setLocation] = useState("");
  const [sessions, setSessions] = useState(""); // comma-joined linked session ids
  const [kidsData, setKidsData] = useState([]);
  const [locationData, setLocationData] = useState([]);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const editing = initialGroupID !== "";

  useEffect(() => {
    if (!signedIn) return;
    const base = db.collection("Main").doc(global.config.uid);

    const unsubKids = base.collection("Kids").onSnapshot((snapshot) => {
      setKidsData(
        snapshot.docs.map((doc) => ({
          id: doc.id,
          fullName: doc.data().FirstName + " " + doc.data().LastName,
        }))
      );
    });

    const unsubLocations = base.collection("Locations").onSnapshot((snapshot) => {
      setLocationData(
        snapshot.docs.map((doc) => ({ id: doc.id, Location: doc.data().Location }))
      );
    });

    if (initialGroupID !== "") {
      base
        .collection("Groups")
        .doc(initialGroupID)
        .get()
        .then((doc) => {
          if (!doc.exists) return;
          setKids(doc.data().Kids || []);
          setLocation(doc.data().Location || "");
          if (doc.data().Date) setDate(doc.data().Date.toDate());
          const sessionIds = doc.data().sessions || "";
          setSessions(sessionIds);
          // Pull each linked session's notes into state, keyed by its kid.
          sessionIds
            .split(",")
            .filter(Boolean)
            .forEach((sid) => {
              base
                .collection("Sessions")
                .doc(sid)
                .get()
                .then((sdoc) => {
                  if (sdoc.exists) {
                    setNotesByKid((prev) => ({
                      ...prev,
                      [sdoc.data().Kid]: sdoc.data().Notes || "",
                    }));
                  }
                })
                .catch((error) => console.error("Error getting session:", error));
            });
        })
        .catch((error) => console.error("Error getting group:", error));
    }

    return () => {
      unsubKids();
      unsubLocations();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn, initialGroupID]);

  const handleKidsChange = (event) => {
    const value = event.target.value;
    setKids(typeof value === "string" ? value.split(",") : value);
  };

  const setNotes = (id, text) => setNotesByKid((prev) => ({ ...prev, [id]: text }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const base = db.collection("Main").doc(global.config.uid);

    try {
      if (groupID !== "") {
        // Update each linked session, then the group record.
        const sessionIds = sessions.split(",").filter(Boolean);
        for (let i = 0; i < sessionIds.length; i++) {
          const id = kidId(kids[i]);
          await base.collection("Sessions").doc(sessionIds[i]).set(
            {
              Notes: notesByKid[id] || "",
              Date: date,
              Location: location,
              Kid: id,
            },
            { merge: true }
          );
        }
        await base.collection("Groups").doc(groupID).set(
          {
            Kids: kids,
            Date: date,
            Location: location,
            fullNames: kids.map(kidName).join(", "),
          },
          { merge: true }
        );
        setToast({ severity: "success", message: "Group session updated" });
      } else {
        // Create one session per kid (first = MainSession), then the group.
        const sessionIds = [];
        for (let i = 0; i < kids.length; i++) {
          const id = kidId(kids[i]);
          const doc = await base.collection("Sessions").add({
            Kid: id,
            Date: date,
            Notes: notesByKid[id] || "",
            Location: location,
            sessionType: i === 0 ? "MainSession" : "GroupSession",
            fullName: kidName(kids[i]),
          });
          sessionIds.push(doc.id);
        }
        const names = kids.map(kidName).join(", ");
        const groupDoc = await base.collection("Groups").add({
          Kids: kids,
          Date: date,
          Location: location,
          fullNames: names,
          sessions: sessionIds.join(","),
        });
        setSessions(sessionIds.join(","));
        setGroupID(groupDoc.id);
        setToast({ severity: "success", message: "Group session saved" });
        history.replace("/group-profile?groupID=" + groupDoc.id);
      }
    } catch (error) {
      console.error("Error saving group session:", error);
      setToast({ severity: "error", message: "Could not save — try again" });
    }
    setSaving(false);
  };

  if (!signedIn) return <Redirect to="/" />;

  const locationOptions =
    location && !locationData.some((o) => o.Location === location)
      ? [...locationData, { id: "current", Location: location }]
      : locationData;

  return (
    <PageContainer maxWidth={860}>
      <Button
        component={Link}
        to="/groups"
        startIcon={<ArrowBack />}
        sx={{ mb: 1, color: palette.textSecondary }}
      >
        Back to Groups
      </Button>
      <PageHeader
        title={editing ? "Edit Group Session" : "Log a Group Session"}
        subtitle="One session with multiple kids — each kid gets their own note"
      />

      <Card>
        <CardContent sx={{ p: 3 }}>
          <form autoCorrect="off" onSubmit={handleSubmit} id="single-form">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <FormControl fullWidth required disabled={editing}>
                <InputLabel id="group-kids-label">Kids in this group</InputLabel>
                <Select
                  labelId="group-kids-label"
                  multiple
                  value={kids}
                  onChange={handleKidsChange}
                  input={<OutlinedInput label="Kids in this group" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          size="small"
                          label={kidName(value)}
                          sx={{
                            backgroundColor: palette.primarySoft,
                            color: palette.primaryDark,
                          }}
                        />
                      ))}
                    </Box>
                  )}
                  MenuProps={{ PaperProps: { style: { maxHeight: 260 } } }}
                >
                  {kidsData.map((option) => (
                    <MenuItem key={option.id} value={option.id + "_" + option.fullName}>
                      <Checkbox
                        checked={kids.indexOf(option.id + "_" + option.fullName) > -1}
                      />
                      <ListItemText primary={option.fullName} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ display: "flex", gap: 2, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
                <TextField
                  required
                  fullWidth
                  type="date"
                  label="Date"
                  value={toInputDate(date)}
                  onChange={(e) => e.target.value && setDate(fromInputDate(e.target.value))}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  select
                  required
                  fullWidth
                  label="Location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                >
                  {locationOptions.map((option) => (
                    <MenuItem key={option.id} value={option.Location}>
                      {option.Location}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              {kids.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Typography variant="h6">Notes per kid</Typography>
                  {kids.map((encoded) => (
                    <TextField
                      key={encoded}
                      required
                      fullWidth
                      multiline
                      minRows={3}
                      label={`${kidName(encoded)} — Session Notes`}
                      value={notesByKid[kidId(encoded)] || ""}
                      onChange={(e) => setNotes(kidId(encoded), e.target.value)}
                    />
                  ))}
                </Box>
              )}

              <Box>
                <Button
                  id="save"
                  variant="contained"
                  color="secondary"
                  size="large"
                  type="submit"
                  disabled={saving || kids.length === 0}
                  startIcon={<Save />}
                >
                  {saving ? "Saving..." : editing ? "Save Changes" : "Save Group Session"}
                </Button>
              </Box>
            </Box>
          </form>
        </CardContent>
      </Card>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ fontWeight: 600 }}>
            {toast.message}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>
    </PageContainer>
  );
}
